import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
// ---------------------------------------------------------------------------
// Token format used by the selector invite-token flow (Wave 2.46 BE-2).
// 64 lowercase hex chars = sha256-hashed by the API; mirrors the password-
// reset token regex in `auth.ts` so frontend validation is symmetric.
// ---------------------------------------------------------------------------
const SELECTOR_INVITE_TOKEN_REGEX = /^[0-9a-f]+$/;
/**
 * Wire schemas for `/admin/selectors/*` — admin-arch §3.8, admin-prd §7.
 *
 * Why a dedicated module (not folded into `auth.ts` despite `internal.users`):
 *   - The shape is admin-facing and carries selector-specific fields
 *     (`payment_details`, `status`, `blocked_at`) which never appear on the
 *     auth surface.
 *   - List / card / patch DTOs do NOT exist for the generic user. Keeping
 *     them here matches the existing pattern (`account.ts`, `tariff.ts`).
 *
 * Selector status semantics (PRD §7.2):
 *   - `active`  — can log in, can upload, published tracks play normally.
 *   - `blocked` — login forbidden (users.is_active=false), published tracks
 *     keep playing (no impact on `content.tracks`). Payouts continue to
 *     accumulate but are gated for manual release.
 */
export const selectorStatusSchema = z.enum(['active', 'blocked']);
// ---------------------------------------------------------------------------
// Payment details — jsonb blob. All fields optional / nullable. The shape is
// validated server-side and stored verbatim. Future fields (paypal, swift)
// can extend the schema without a migration.
// ---------------------------------------------------------------------------
export const selectorPaymentDetailsSchema = z
    .object({
    bank_name: z.string().trim().max(255).nullable().optional(),
    account_number: z
        .string()
        .trim()
        .regex(/^\d{20}$/, 'Bank account number must be 20 digits')
        .nullable()
        .optional(),
    inn: z
        .string()
        .trim()
        .regex(/^\d{10}(\d{2})?$/, 'INN must be 10 or 12 digits')
        .nullable()
        .optional(),
    kpp: z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'KPP must be 9 digits')
        .nullable()
        .optional(),
    bik: z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'BIK must be 9 digits')
        .nullable()
        .optional(),
    correspondent_account: z
        .string()
        .trim()
        .regex(/^\d{20}$/, 'Correspondent account must be 20 digits')
        .nullable()
        .optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// List item (admin-prd §7.2). Minimal columns for the DataTable.
// MVP: stats (uploaded/published/rejected/owed) are deferred to a follow-up
// task — payouts table does not yet exist.
// ---------------------------------------------------------------------------
export const selectorListItemSchema = z.object({
    id: uuidSchema,
    user_id: uuidSchema,
    email: z.string(),
    name: z.string(),
    status: selectorStatusSchema,
    contract_signed_at: z.string().nullable(),
    created_at: isoDateTimeSchema,
    blocked_at: isoDateTimeSchema.nullable(),
});
export const selectorListResponseSchema = z.object({
    items: z.array(selectorListItemSchema),
    total: z.number().int().nonnegative().nullable(),
    next_cursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// List filter / pagination. Keyset cursor mirrors `accountFilterSchema`.
// ---------------------------------------------------------------------------
const Q_MAX = 200;
const CURSOR_MAX = 200;
export const selectorFilterSchema = z.object({
    q: z.string().min(1).max(Q_MAX).optional(),
    status: selectorStatusSchema.optional(),
    limit: z.coerce.number().int().positive().max(100).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
// ---------------------------------------------------------------------------
// Card response (admin-prd §7.2). Full selector profile.
// MVP: stats / payouts / activity blocks deferred to follow-up task. Tracks
// + presets tabs live behind their own endpoints (caller hits
// /admin/tracks?uploaded_by=:user_id etc).
// ---------------------------------------------------------------------------
export const selectorCardSchema = z.object({
    id: uuidSchema,
    user_id: uuidSchema,
    email: z.string(),
    name: z.string(),
    contact_email: z.string().nullable(),
    contact_phone: z.string().nullable(),
    status: selectorStatusSchema,
    contract_signed_at: z.string().nullable(),
    payment_details: selectorPaymentDetailsSchema.nullable(),
    // BUG-023 follow-up: surface the payout-rate column added in migration
    // 0064 so the selector card "Profile" tab can both display and edit it.
    // Backed by `internal.selectors.payout_rate_per_period` (NOT NULL DEFAULT
    // 0, CHECK 0..100). Stored as integer percent.
    payout_rate_per_period: z.number().int().min(0).max(100),
    blocked_at: isoDateTimeSchema.nullable(),
    created_at: isoDateTimeSchema,
    updated_at: isoDateTimeSchema,
    last_login_at: isoDateTimeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Create (admin-prd §7.1 US-SEL-1, acceptance: invite email contains only
// login + temp password + admin URL).
// ---------------------------------------------------------------------------
export const selectorCreateSchema = z
    .object({
    email: z.string().trim().email().max(255),
    name: z.string().trim().min(1).max(200),
    contact_phone: z.string().trim().max(64).nullable().optional(),
    contract_signed_at: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'contract_signed_at must be YYYY-MM-DD')
        .nullable()
        .optional(),
    payment_details: selectorPaymentDetailsSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Patch (admin-prd §7.2). Email NOT editable — collateral with login. New
// email requires a separate re-invite flow.
// ---------------------------------------------------------------------------
export const selectorPatchSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    contact_email: z.string().trim().email().max(255).nullable().optional(),
    contact_phone: z.string().trim().max(64).nullable().optional(),
    contract_signed_at: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'contract_signed_at must be YYYY-MM-DD')
        .nullable()
        .optional(),
    payment_details: selectorPaymentDetailsSchema.nullable().optional(),
    // BUG-023 follow-up: admin edits the payout rate from the selector
    // card Profile tab. Integer percent, 0..100. NOT nullable — the column
    // is NOT NULL DEFAULT 0; omit the key to leave it unchanged.
    payout_rate_per_period: z.number().int().min(0).max(100).optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
// ---------------------------------------------------------------------------
// Invite-token flow (Wave 2.46 BE-2, TASK-078-followup).
//
// Replaces the original temp-password onboarding (Wave 2.26 BE-1) with a
// single-use token mailed to the selector. Three surfaces:
//   - `POST /admin/selectors/:id/resend-invite` — admin-gated, regenerates
//     the token and re-mails the invite. No body needed.
//   - `POST /public/selectors/accept-invite`    — public, body carries
//     `{ token, new_password }`; sets the selector's password and consumes
//     the token. 410 GONE on invalid / expired / consumed token.
//   - `GET  /admin/selectors/:id/stats`         — admin-gated, returns
//     aggregated counts + earnings for the selector card "Stats" tab.
//
// `resend-invite` returns the same payload shape as `selectorCardSchema`
// for parity with the create endpoint.
// ---------------------------------------------------------------------------
/**
 * `POST /admin/selectors/:id/resend-invite` response.
 *
 * `expires_at` is the new token's expiry (7 days from issue). In dev/test
 * the link itself is returned via `invite_link_dev_only` so the QA flow
 * can be completed without a working SMTP relay — mirrors
 * `AdminInviteResponse`. Never set in production.
 */
export const selectorInviteResponseSchema = z.object({
    selector_id: uuidSchema,
    expires_at: isoDateTimeSchema,
    invite_link_dev_only: z.string().url().optional(),
});
/**
 * `POST /public/selectors/accept-invite` body.
 *
 * Password policy mirrors `passwordResetConfirmSchema` (min 12, max 255).
 * Token format mirrors the password-reset token: 64 hex lowercase chars
 * (`crypto.randomBytes(32).toString('hex')`).
 */
export const selectorAcceptInviteSchema = z
    .object({
    token: z
        .string()
        .length(64, 'Token must be 64 hex chars')
        .regex(SELECTOR_INVITE_TOKEN_REGEX, 'Token must be lowercase hex'),
    new_password: z.string().min(12).max(255),
})
    .strict();
/**
 * Error body returned by `/public/selectors/accept-invite` with HTTP 410
 * Gone when the token is unknown, expired, or already consumed. The three
 * cases are intentionally indistinguishable so a probing client cannot
 * enumerate live tokens. Mirrors `passwordResetInvalidTokenErrorSchema`.
 */
export const selectorInviteInvalidTokenErrorSchema = z.object({
    code: z.literal('INVALID_TOKEN'),
    message: z.string(),
});
/**
 * `GET /admin/selectors/:id/stats` response.
 *
 * Aggregations:
 *   - `own_presets_count` — `content.presets` rows where
 *     `author_type='selector' AND author_id=<selector.user_id> AND
 *     deleted_at IS NULL`.
 *   - `own_promo_codes_count` — `internal.promo_codes` rows where
 *     `owner_selector_id=<selector.id>` (regardless of `is_active`).
 *   - `total_earned_kop` — SUM(`customers.promo_code_activations
 *     .total_selector_payout_kop`) joined to promo codes owned by this
 *     selector. `null` when there are no activations yet.
 *   - `last_active_at` — MAX(`internal.user_sessions.created_at`) for
 *     the selector's user. `null` when the selector has never logged in.
 *
 * MVP scope: this is the minimum set the FE Stats tab consumes. Track
 * counts (uploaded / published / rejected) are deferred to a separate
 * follow-up keyed to the payouts table.
 */
export const selectorStatsResponseSchema = z.object({
    selector_id: uuidSchema,
    own_presets_count: z.number().int().nonnegative(),
    own_promo_codes_count: z.number().int().nonnegative(),
    total_earned_kop: z.number().int().nonnegative(),
    last_active_at: isoDateTimeSchema.nullable(),
});
//# sourceMappingURL=selector.js.map